import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  Database,
  Users,
  Crosshair,
  Lightbulb,
  FileText,
  ArrowRight,
  Cpu,
  Clock,
  Coins,
  MousePointerClick,
} from "lucide-react";
import type { Prompt } from "@/api/prompt-management";

// ── Pipeline step metadata ──

interface PipelineStep {
  step: number;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
  dataFlowIn: string[];
  dataFlowOut: string[];
}

const PIPELINE_STEPS: PipelineStep[] = [
  {
    step: 2,
    label: "Step 2: Data Aggregation",
    shortLabel: "資料聚合",
    icon: Database,
    color: "text-slate-700",
    bgColor: "bg-slate-50",
    borderColor: "border-slate-300",
    dataFlowIn: ["SERP Results"],
    dataFlowOut: ["${competitors_xml}"],
  },
  {
    step: 3,
    label: "Step 3: Competitor Analysis",
    shortLabel: "競爭者分析",
    icon: Users,
    color: "text-blue-700",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-300",
    dataFlowIn: ["${keyword}", "${competitors_xml}"],
    dataFlowOut: ["${step3_output}"],
  },
  {
    step: 4,
    label: "Step 4: User Intent",
    shortLabel: "使用者意圖",
    icon: Crosshair,
    color: "text-green-700",
    bgColor: "bg-green-50",
    borderColor: "border-green-300",
    dataFlowIn: ["${keyword}"],
    dataFlowOut: ["${step4_output}"],
  },
  {
    step: 5,
    label: "Step 5: Opportunity Analysis",
    shortLabel: "機會分析",
    icon: Lightbulb,
    color: "text-purple-700",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-300",
    dataFlowIn: ["${step3_output}", "${step4_output}"],
    dataFlowOut: ["${step5_output}"],
  },
  {
    step: 6,
    label: "Step 6: Content Outline",
    shortLabel: "內容大綱",
    icon: FileText,
    color: "text-orange-700",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-300",
    dataFlowIn: ["${step5_output}"],
    dataFlowOut: ["JSON Report"],
  },
];

// ── Node component ──

function PipelineNode({
  stepDef,
  prompt,
  onClick,
}: {
  stepDef: PipelineStep;
  prompt: Prompt | null;
  onClick: () => void;
}) {
  const Icon = stepDef.icon;
  const isCodeStep = stepDef.step === 2;

  const modelLabel = prompt?.ai_model?.split("/").pop() || (isCodeStep ? "Code" : "—");
  const avgDuration = prompt?.avg_duration_ms;
  const avgCost = prompt?.avg_cost;
  const usageCount = prompt?.usage_count || 0;

  // Status color
  let statusDot = "bg-gray-400"; // default: no data
  if (prompt) {
    if (prompt.is_active) statusDot = "bg-green-500";
    else statusDot = "bg-yellow-500";
  }
  if (isCodeStep) statusDot = "bg-green-500";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className={cn(
              "relative flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all cursor-pointer",
              "hover:shadow-md hover:scale-[1.03] active:scale-100",
              stepDef.bgColor,
              stepDef.borderColor,
              "min-w-[120px]",
            )}
          >
            {/* Status dot */}
            <div className={cn("absolute top-2 right-2 w-2.5 h-2.5 rounded-full", statusDot)} />

            <Icon className={cn("h-6 w-6", stepDef.color)} />
            <span className={cn("text-xs font-semibold", stepDef.color)}>{stepDef.shortLabel}</span>

            {/* Model */}
            <Badge variant="outline" className="text-[10px] font-mono h-5">
              <Cpu className="h-2.5 w-2.5 mr-0.5" />
              {modelLabel}
            </Badge>

            {/* Stats row */}
            {!isCodeStep && (
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                {avgDuration != null && avgDuration > 0 && (
                  <span className="flex items-center gap-0.5">
                    <Clock className="h-2.5 w-2.5" />
                    {(avgDuration / 1000).toFixed(1)}s
                  </span>
                )}
                {avgCost != null && avgCost > 0 && (
                  <span className="flex items-center gap-0.5">
                    <Coins className="h-2.5 w-2.5" />
                    {Number(avgCost).toFixed(4)}
                  </span>
                )}
              </div>
            )}

            {/* Usage count */}
            {usageCount > 0 && (
              <span className="text-[10px] text-muted-foreground">{usageCount} runs</span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <p className="font-medium text-sm">{stepDef.label}</p>
          {prompt && <p className="text-xs text-muted-foreground">v{prompt.version} — {prompt.ai_model}</p>}
          <div className="mt-1 text-xs">
            <span className="text-muted-foreground">In:</span>{" "}
            {stepDef.dataFlowIn.map(d => (
              <code key={d} className="bg-muted px-1 rounded text-[10px] mr-1">{d}</code>
            ))}
          </div>
          <div className="text-xs">
            <span className="text-muted-foreground">Out:</span>{" "}
            {stepDef.dataFlowOut.map(d => (
              <code key={d} className="bg-muted px-1 rounded text-[10px] mr-1">{d}</code>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
            <MousePointerClick className="h-3 w-3" />
            {isCodeStep ? "Pure code step (no prompt)" : "Click to edit prompt"}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Arrow connector ──

function FlowArrow({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center px-1">
      <ArrowRight className="h-4 w-4 text-muted-foreground" />
      {label && (
        <span className="text-[9px] text-muted-foreground font-mono max-w-[80px] text-center leading-tight">
          {label}
        </span>
      )}
    </div>
  );
}

// ── Main Pipeline component ──

interface PromptPipelineProps {
  prompts: Prompt[];
  onEditPrompt: (prompt: Prompt) => void;
}

export default function PromptPipeline({ prompts, onEditPrompt }: PromptPipelineProps) {
  // Map step_number → active prompt
  const promptMap: Record<number, Prompt> = {};
  for (const p of prompts) {
    if (p.is_active && (!promptMap[p.step_number] || p.version > promptMap[p.step_number].version)) {
      promptMap[p.step_number] = p;
    }
  }

  const handleNodeClick = (stepDef: PipelineStep) => {
    const prompt = promptMap[stepDef.step];
    if (prompt) {
      onEditPrompt(prompt);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-semibold">SERP Analysis Pipeline</h3>
        <Badge variant="outline" className="text-[10px]">5 steps</Badge>
        <div className="flex items-center gap-3 ml-auto text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
            Active
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" />
            Inactive
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-gray-400 inline-block" />
            No data
          </span>
        </div>
      </div>

      {/* Pipeline flow diagram */}
      {/* Row 1: Step 2 → Step 3 ──→ Step 5 → Step 6 */}
      {/* Row 2:          Step 4 ──┘                   */}
      <div className="flex flex-col items-center gap-2">
        {/* Main row */}
        <div className="flex items-center gap-1 flex-wrap justify-center">
          <PipelineNode
            stepDef={PIPELINE_STEPS[0]}
            prompt={null}
            onClick={() => {}}
          />
          <FlowArrow label="${competitors_xml}" />
          <PipelineNode
            stepDef={PIPELINE_STEPS[1]}
            prompt={promptMap[3] || null}
            onClick={() => handleNodeClick(PIPELINE_STEPS[1])}
          />
          <div className="flex flex-col items-center gap-0.5">
            <FlowArrow label="${step3_output}" />
            <span className="text-[8px] text-muted-foreground">parallel ↕</span>
          </div>
          <PipelineNode
            stepDef={PIPELINE_STEPS[3]}
            prompt={promptMap[5] || null}
            onClick={() => handleNodeClick(PIPELINE_STEPS[3])}
          />
          <FlowArrow label="${step5_output}" />
          <PipelineNode
            stepDef={PIPELINE_STEPS[4]}
            prompt={promptMap[6] || null}
            onClick={() => handleNodeClick(PIPELINE_STEPS[4])}
          />
        </div>

        {/* Step 4 branch (parallel with Step 3) */}
        <div className="flex items-center gap-1 -mt-1">
          <div className="w-[120px]" /> {/* spacer for Step 2 */}
          <div className="w-[40px]" /> {/* spacer for arrow */}
          <PipelineNode
            stepDef={PIPELINE_STEPS[2]}
            prompt={promptMap[4] || null}
            onClick={() => handleNodeClick(PIPELINE_STEPS[2])}
          />
          <div className="flex items-center">
            <span className="text-[9px] text-muted-foreground font-mono ml-2">
              ↗ ${"{step4_output}"}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
